import CloseIcon from "assets/images/CloseIcon";
import {useEffect, useState} from "react";
import ReactModal from "react-modal";
import {medicationsFilterModal as strings} from "strings";

import Button from "components/Button";
import Switch from "components/Switch";

import styles from "./MedicationsFilterModal.module.css";

export type FilterOptions = {
  readyForRefill: boolean;
  requiresRenewal: boolean;
  scheduled: boolean;
  readyForPickup: boolean;
  onFile: boolean;
  inProgress: boolean;
};

type Props = {
  visible: boolean;
  filterOptions: FilterOptions;
  onClickConfirm: (filterOptions: FilterOptions) => void;
  onClickCancel: () => void;
};

export const DEFAULT_FILTERS: FilterOptions = {
  readyForRefill: false,
  requiresRenewal: false,
  scheduled: false,
  readyForPickup: false,
  onFile: false,
  inProgress: false,
};

function SwitchRow({
  name,
  value,
  onChange,
}: {
  name: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div key={name} className={styles.switchItem}>
      <span className={styles.bodyText}>{name}</span>
      <Switch checked={value} onChange={onChange} />
    </div>
  );
}

function FilterControls({
  startingOpts,
  onClickConfirm,
  onClickCancel,
}: {
  startingOpts: FilterOptions;
  onClickConfirm: (filterOptions: FilterOptions) => void;
  onClickCancel: () => void;
}) {
  const [filters, setFilters] = useState<FilterOptions>({
    readyForPickup: startingOpts.readyForPickup,
    scheduled: startingOpts.scheduled,
    requiresRenewal: startingOpts.requiresRenewal,
    readyForRefill: startingOpts.readyForRefill,
    onFile: startingOpts.onFile,
    inProgress: startingOpts.inProgress,
  });

  const [hasChanged, setHasChanged] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);

  useEffect(() => {
    const isDifferentFromDefault =
      filters.readyForPickup !== DEFAULT_FILTERS.readyForPickup ||
      filters.scheduled !== DEFAULT_FILTERS.scheduled ||
      filters.requiresRenewal !== DEFAULT_FILTERS.requiresRenewal ||
      filters.readyForRefill !== DEFAULT_FILTERS.readyForRefill ||
      filters.onFile !== DEFAULT_FILTERS.onFile ||
      filters.inProgress !== DEFAULT_FILTERS.inProgress;
    setHasChanged(isDifferentFromDefault);
    const isDifferentFromParams =
      filters.readyForPickup !== startingOpts.readyForPickup ||
      filters.scheduled !== startingOpts.scheduled ||
      filters.requiresRenewal !== startingOpts.requiresRenewal ||
      filters.readyForRefill !== startingOpts.readyForRefill ||
      filters.onFile !== startingOpts.onFile ||
      filters.inProgress !== startingOpts.inProgress;
    setCanUpdate(isDifferentFromParams);
  }, [startingOpts, filters]);

  return (
    <div className={styles.filterControls}>
      <h3 className={styles.filterControlSection}>{strings.filterByStatus}</h3>
      <div>
        <SwitchRow
          name={strings.readyForPickup}
          value={filters.readyForPickup}
          onChange={(val) =>
            setFilters((prev) => ({...prev, readyForPickup: val}))
          }
        />
        <SwitchRow
          name={strings.inProgress}
          value={filters.inProgress}
          onChange={(val) => setFilters((prev) => ({...prev, inProgress: val}))}
        />
        <SwitchRow
          name={strings.scheduled}
          value={filters.scheduled}
          onChange={(val) => setFilters((prev) => ({...prev, scheduled: val}))}
        />
        <SwitchRow
          name={strings.requiresRenewal}
          value={filters.requiresRenewal}
          onChange={(val) =>
            setFilters((prev) => ({...prev, requiresRenewal: val}))
          }
        />
        <SwitchRow
          name={strings.readyForRefill}
          value={filters.readyForRefill}
          onChange={(val) =>
            setFilters((prev) => ({...prev, readyForRefill: val}))
          }
        />
        <SwitchRow
          name={strings.onFile}
          value={filters.onFile}
          onChange={(val) => setFilters((prev) => ({...prev, onFile: val}))}
        />
      </div>
      <Button
        className={styles.applyFilterButton}
        disabled={!canUpdate}
        onClick={() => {
          onClickConfirm(filters);
          onClickCancel();
        }}
        variant="filled"
      >
        {strings.update}
      </Button>
      <Button
        className={styles.applyFilterButton}
        disabled={!hasChanged}
        onClick={() => {
          setFilters(DEFAULT_FILTERS);
        }}
        variant="unfilled"
      >
        {strings.reset}
      </Button>
    </div>
  );
}

export default function MedicationsFilterModal({
  filterOptions,
  ...props
}: Props) {
  return (
    <ReactModal
      className={styles.modal}
      overlayClassName={styles.overlay}
      isOpen={props.visible}
      shouldCloseOnOverlayClick={true}
      onRequestClose={props.onClickCancel}
    >
      <div className={styles.header}>
        <button className={styles.xButton} onClick={props.onClickCancel}>
          <CloseIcon />
        </button>
        <h1 className={styles.title}>{strings.title}</h1>
      </div>
      <FilterControls
        startingOpts={filterOptions}
        onClickConfirm={props.onClickConfirm}
        onClickCancel={props.onClickCancel}
      />
    </ReactModal>
  );
}
